/// <amd-module name="Wit/Common/WitSettingsViews/Components/StylesTable/StyleService" />
import {
    IObservableArray,
    IObservableValue,
    IReadyableObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FilterClause, Rule } from "Wit/Clients/Work/Contracts/Work";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { CardRule, IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getWiql } from "Wit/Common/PredicateWIT";
import {
    TodayMacros,
    getInvariantCurrentIterationMacro,
    getInvariantOperator,
    getInvariantTodayMacro,
    getLocalizedCurrentIterationMacro,
    getLocalizedOperator,
    getLocalizedTodayMacro,
    isCurrentIterationMacro,
    isTodayMacro
} from "Wit/Common/WiqlOperators";
import { parseCurrentIteration, parseTodayMacro } from "Wit/Common/WiqlValues";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { getListBoxItemsFromFields, getSupportedFieldDefinitions } from "../../Utils/FieldUtils";
import { ICriteriaSelectedSettingData, ICriteriaSettingData } from "../CriteriaTable/CriteriaInterfaces";
import { getCriteriaSelectedSettingData, getFieldCriteriaAsFilterClauses } from "../CriteriaTable/CriteriaService";
import { StyleCriteriaConstants } from "../StylesTable/StyleCriteriaConstants";
import { IRuleData, IStylesRuleData } from "./StyleInterfaces";

export function getStyleSettingsAsStyleRules(settings: IStylesRuleData[]): Rule[] {
    return settings.map(setting => {
        const clauses = getFieldCriteriaAsFilterClauses(setting.criteriaData.value as ICriteriaSelectedSettingData[]);
        const styleConfiguration = getSettingsDetails(setting);
        return {
            name: setting.ruleName.value,
            isEnabled: setting.isEnabled.value.toString(),
            filter: getWiql({ groups: [], clauses, maxGroupLevel: 0 }),
            clauses,
            settings: styleConfiguration
        };
    });
}

function getSettingsDetails(setting: IStylesRuleData) {
    const settings: any = { "background-color": setting.color.value, "title-color": setting.titleColor.value };
    if (setting.isTitleBold.value) {
        settings["title-font-weight"] = "bold";
    }
    if (setting.isTitleItalicized.value) {
        settings["title-font-style"] = "italic";
    }
    if (setting.isTitleUnderlined.value) {
        settings["title-text-decoration"] = "underline";
    }
    return settings;
}

export function getStyleSettingData(styles: Rule[]): IStylesRuleData[] {
    return styles.map(style => {
        return {
            ruleName: new ObservableValue<string>(style.name),
            isEnabled: new ObservableValue<boolean>(style.isEnabled.toLowerCase() === "true"),
            filter: style.filter,
            color: new ObservableValue<string>((style.settings as any)["background-color"]),
            titleColor: new ObservableValue<string>((style.settings as any)["title-color"] ?? "#000000"),
            isTitleItalicized: new ObservableValue<boolean>(!!(style.settings as any)["title-font-style"]),
            isTitleBold: new ObservableValue<boolean>(!!(style.settings as any)["title-font-weight"]),
            isTitleUnderlined: new ObservableValue<boolean>(!!(style.settings as any)["title-text-decoration"]),
            id: newGuid(),
            selectedCriteriaData: getCriteriaSelectedSettingData(style.clauses),
            criteriaData: new ObservableArray<ICriteriaSettingData>([])
        };
    });
}

export function getStyleSettingDataFromCardRules(styles: CardRule[]): IStylesRuleData[] {
    return styles.map(style => {
        return {
            ruleName: new ObservableValue<string>(style.name),
            isEnabled: new ObservableValue<boolean>(style.isEnabled),
            color: new ObservableValue<string>((style.style as any)["background-color"]),
            titleColor: new ObservableValue<string>((style.style as any)["title-color"] ?? "#000000"),
            isTitleItalicized: new ObservableValue<boolean>(!!(style.style as any)["title-font-style"]),
            isTitleBold: new ObservableValue<boolean>(!!(style.style as any)["title-font-weight"]),
            isTitleUnderlined: new ObservableValue<boolean>(!!(style.style as any)["title-text-decoration"]),
            id: newGuid(),
            selectedCriteriaData: getCriteriaSelectedSettingData(style.criteria["clauses"] as FilterClause[]),
            criteriaData: new ObservableArray<ICriteriaSettingData>([])
        };
    });
}

export interface IStyleService extends IVssService {
    /**
     * Error message from data provider failure
     */
    dataProviderErrorMessage: IObservableValue<string | undefined>;

    /**
     * Observable that indicates when field values have been loaded
     */
    fieldsReady: IObservableValue<boolean>;

    /**
     * Tracks if data has changed from rules initially supplied
     */
    isDirty: IObservableValue<boolean>;

    /**
     * Tracks if data is valid
     */
    isValid: IObservableValue<boolean>;

    /**
     * Observable list of configured rules
     */
    rows: IObservableArray<IStylesRuleData>;

    /**
     * Error message from invalid settings
     */
    validationErrorMessage: IObservableValue<string | undefined>;

    /**
     * Async add a new rule row with default values
     */
    addRuleRow(rule?: IStylesRuleData): Promise<void>;

    /**
     * Async add a new criteria settings row with default values
     */
    addCriteriaSettingsRow(criteriaSetting?: ICriteriaSelectedSettingData, i?: number): Promise<void>;

    /**
     * Set style settings rows
     *
     * Note: this is intended to be used a single time when the component is passed existing rules data
     */
    setStyleSettingRows(stylesSettingsData: IStylesRuleData[]): void;

    /**
     * Clear the current rules settings
     */
    clear(): void;

    /**
     * Remove a criteria settings entry
     */
    removeCriteriaRow(index: number, item: ICriteriaSettingData): void;

    /**
     * Remove a rule entry
     */
    removeRuleRow(index: number): void;

    /**
     * Update the name of a rule
     */
    updateName(index: number, name: string, preserveSelection?: boolean): void;

    /**
     * Update if rule is enabled
     */
    updateIsEnabled(index: number, isEnabled: boolean, skipValidation?: boolean): void;

    /**
     * Update if title text is bold
     */
    updateisTitleBold(index: number, isTitleBold: boolean, skipValidation?: boolean): void;

    /**
     * Update if title text is italicizied
     */
    updateisTitleItalicized(index: number, isTitleItalicized: boolean, skipValidation?: boolean): void;

    /**
     * Update if title text is underlined
     */
    updateisTitleUnderlined(index: number, isTitleUnderlined: boolean, skipValidation?: boolean): void;

    /**
     * Update the color of a title
     */
    updateTitleColor(index: number, color: string, skipValidation?: boolean): void;

    /**
     * Update the color of a rule
     */
    updateColor(index: number, color: string, skipValidation?: boolean): void;

    /**
     * Update the field of a criteria setting
     */
    updateField(fieldReferenceName: string, item: ICriteriaSettingData, preserveSelection?: boolean): void;

    /**
     * Update the value of a criteria setting
     */
    updateValue(value: string, item: ICriteriaSettingData, skipValidation?: boolean): void;

    /**
     * Update the operator of a criteria setting
     */
    updateOperator(operator: string, item: ICriteriaSettingData, skipValidation?: boolean): void;

    /**
     * Check if drag and drop has changed
     */
    validateDragAndDrop(): void;
}

class StyleService extends VssService implements IStyleService {
    private _dataProviderErrorMessage: IObservableValue<string | undefined>;
    private _isDirty: IObservableValue<boolean>;
    private _isValid: IObservableValue<boolean>;
    private _rows: IObservableArray<IStylesRuleData>;
    private _validationErrorMessage: IObservableValue<string | undefined>;

    private _initialized: Promise<void>;
    private _defaultField: IFieldEntry;
    private _fieldListBoxItems: IReadyableObservableArray<IListBoxItem>;
    private _useNewFieldService: boolean;
    private _fieldService: IWorkItemFieldService;
    private _fieldService2: IWorkItemFieldService2;
    private _operators: { [fieldReferenceName: string]: string[] };
    private _originalRules: IRuleData[];
    private _originalCriteria: ICriteriaSelectedSettingData[][];

    public get dataProviderErrorMessage(): IObservableValue<string | undefined> {
        return this._dataProviderErrorMessage;
    }

    public get fieldsReady(): IObservableValue<boolean> {
        return this._fieldListBoxItems.ready;
    }

    public get isDirty(): IObservableValue<boolean> {
        return this._isDirty;
    }

    public get isValid(): IObservableValue<boolean> {
        return this._isValid;
    }

    public get rows(): IObservableArray<IStylesRuleData> {
        return this._rows;
    }

    public get validationErrorMessage(): IObservableValue<string | undefined> {
        return this._validationErrorMessage;
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this._dataProviderErrorMessage = new ObservableValue<string | undefined>(undefined);
        this._isDirty = new ObservableValue<boolean>(false);
        this._isValid = new ObservableValue<boolean>(true);
        this._rows = new ObservableArray<IStylesRuleData>([]);
        this._validationErrorMessage = new ObservableValue<string | undefined>(undefined);

        this._fieldListBoxItems = new ReadyableObservableArray([]);
        this._useNewFieldService = isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false);
        if (this._useNewFieldService) {
            this._fieldService2 = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
        } else {
            this._fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        }
        this._operators = {};
        this._originalCriteria = [];
        this._originalRules = [];

        this._initialized = this._initialize();
    }

    public async addRuleRow(rule?: IStylesRuleData) {
        await this._initialized;
        const newRow = this._getDefaultRule(rule);
        this._rows.push(newRow);
    }

    public async setStyleSettingRows(styleSettings: IStylesRuleData[]) {
        // save original rule for dirty comparisons
        this._originalRules = [];
        this.clear();
        for (var i = 0; i < styleSettings.length; i++) {
            var rule = styleSettings[i];
            this._originalRules.push({
                ruleName: rule.ruleName.value,
                isEnabled: rule.isEnabled.value,
                color: rule.color.value,
                titleColor: rule.titleColor.value,
                isTitleBold: rule.isTitleBold.value,
                isTitleItalicized: rule.isTitleItalicized.value,
                isTitleUnderlined: rule.isTitleUnderlined.value
            });
        }
        styleSettings.forEach(rule => this.addRuleRow(rule));
    }

    public clear() {
        this._rows.value = [];
        this._validationErrorMessage.value = undefined;
        this._dataProviderErrorMessage.value = undefined;
        this._isValid.value = true;
        this._isDirty.value = false;
        this._originalCriteria.length = 0;
    }

    public removeRuleRow(rowIndex: number) {
        const rows = this._rows.value;
        if (rows) {
            rows.splice(rowIndex, 1);
            this._rows.value = rows;
        }
        // removing a row can't put model into an error state
        this._validate();
    }

    public removeCriteriaRow(index: number, item: ICriteriaSettingData) {
        const rows = this._rows.value.find(row => row.criteriaData.value.indexOf(item) > -1);
        const ind = this._rows.value.findIndex(row => row.criteriaData.value.indexOf(item) > -1);
        if (rows) {
            rows.criteriaData.splice(index, 1);
            this._rows.value[ind].criteriaData.value = rows.criteriaData.value;
            this._validate();
        }
    }

    public updateName(index: number, value: string, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.ruleName.value = value;
        !skipValidation && this._validate();
    }

    public updateColor(index: number, value: string, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.color.value = value;
        !skipValidation && this._validate();
    }

    public updateTitleColor(index: number, value: string, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.titleColor.value = value;
        !skipValidation && this._validate();
    }

    public updateIsEnabled(index: number, value: boolean, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.isEnabled.value = value;
        this._setIsDirty();
    }

    public updateisTitleBold(index: number, value: boolean, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.isTitleBold.value = value;
        this._setIsDirty();
    }

    public updateisTitleItalicized(index: number, value: boolean, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.isTitleItalicized.value = value;
        this._setIsDirty();
    }

    public updateisTitleUnderlined(index: number, value: boolean, skipValidation?: boolean) {
        const ruleSettingData = this._rows.value[index];
        ruleSettingData.isTitleUnderlined.value = value;
        this._setIsDirty();
    }

    public validateDragAndDrop() {
        this._setIsDirty();
    }

    private async _initialize() {
        const fields = this._useNewFieldService
            ? await this._fieldService2.loadFields()
            : await this._fieldService.getFieldsAsync();
        if (fields) {
            const supportedFields = getSupportedFieldDefinitions(
                fields,
                StyleCriteriaConstants.supportedFieldTypes,
                StyleCriteriaConstants.notSupportedFieldReferenceNames,
                true // include ignored values (Area Level and Iteration Level)
            );
            this._fieldListBoxItems.value = getListBoxItemsFromFields(supportedFields);
            this._fieldListBoxItems.ready.value = true;
            this._defaultField = supportedFields[0];
            this._getOperators(this._defaultField);
        } else {
            this._dataProviderErrorMessage.value = this._useNewFieldService 
                    ? this._fieldService2.getFieldsErrorMessage() 
                    : this._fieldService.getFieldsErrorMessage();
        }
    }

    private _getOperators(field: IFieldEntry): string[] {
        let operators = this._operators[field.referenceName];
        if (!operators) {
            operators = this._getLocalizedSupportedWiqlOperators(field);
            this._operators[field.referenceName] = operators;
        }

        return operators;
    }

    private _validate() {
        if (this._validationErrorMessage.value !== undefined) {
            this._validationErrorMessage.value = undefined;
        }
        this._validateName();
        this._validateCriteria();

        this._setIsDirty();
        this._setIsValid();
    }

    private _validateName() {
        const settings = this._rows.value;
        // check duplicate names
        const nameMap = settings.map(i => i.ruleName.value.trim());
        var hasDuplicates = nameMap.some((item, idx) => nameMap.indexOf(item) != idx);
        if (hasDuplicates) {
            this._validationErrorMessage.value = Resources.DuplicateNameErrorMessage;
        } else if (settings.findIndex(s => !s.ruleName.value || s.ruleName.value === "") >= 0) {
            //empty to show that the model isn't valid, but there is no error message to display
            this._validationErrorMessage.value = "";
        }
    }

    public async addCriteriaSettingsRow(criteriaSetting?: ICriteriaSelectedSettingData, i?: number) {
        await this._initialized;
        const newRow = this._getDefaultCriteria(criteriaSetting);

        // if no index given, insert at end of row
        if (!i && i != 0) {
            i = this._rows.length - 1;
        }

        this._rows.value[i].criteriaData.push(newRow);
        const index = this._rows.length - 1;
        const innerIndex = this.rows.value[i].criteriaData.value.length - 1;

        // this will only make async fetch when the first row is added
        await this.updateField(newRow.field, this.rows.value[i].criteriaData.value[innerIndex], !!criteriaSetting);
    }

    public async updateField(fieldReferenceName: string, item: ICriteriaSettingData, preserveSelection?: boolean) {
        // make sure field data is loaded
        await this._initialized;
        const field = this._useNewFieldService
                ? this._fieldService2.getFieldByReferenceName(fieldReferenceName)
                : this._fieldService.getFieldByReferenceName(fieldReferenceName);
        if (!field) {
            return;
        }

        const criteriaSettingsData = item;
        const fieldIndex = criteriaSettingsData.fields.value.findIndex(item => item.id === fieldReferenceName);
        criteriaSettingsData.field = fieldReferenceName;
        criteriaSettingsData.fieldSelection.select(fieldIndex);

        // clear selections
        criteriaSettingsData.operatorSelection.clear();

        // update operator options
        const operatorsForField = this._getOperators(field);
        let indexToSelect = 0;
        if (preserveSelection) {
            const operatorIndex = operatorsForField.findIndex(operator => operator === criteriaSettingsData.operator);
            indexToSelect = operatorIndex >= 0 ? operatorIndex : indexToSelect;
        }

        const operator = operatorsForField[indexToSelect];
        this.updateOperator(operator, item, /** skipValidation */ true);
        criteriaSettingsData.operators.value = this._getListBoxItems(operatorsForField);
        criteriaSettingsData.operatorSelection.select(indexToSelect);

        // update allowed values
        !preserveSelection && this.updateValue("", item, /** skipValidation */ true);
        await this._updateAllowedValues(criteriaSettingsData, preserveSelection);

        this._validate();
    }

    private async _addCriteriaSettingsRows(criteriaSettings: ICriteriaSelectedSettingData[]) {
        // save criteria for dirty comparisons
        if (!this._originalCriteria) {
            this._originalCriteria = [criteriaSettings];
        } else {
            this._originalCriteria.push(criteriaSettings);
        }
        const index = this._originalCriteria.length - 1;
        criteriaSettings.forEach(criteria => this.addCriteriaSettingsRow(criteria, index));
    }

    private async _updateAllowedValues(settings: ICriteriaSettingData, preserveSelection?: boolean) {
        settings.criteriaValues.ready.value = false;
        const field =  this._useNewFieldService
                ? this._fieldService2.getFieldByReferenceName(settings.field)
                : this._fieldService.getFieldByReferenceName(settings.field);
        if (!field) {
            return;
        }

        const allowedValues = await this._getAllowedValues(field);

        // we might not have a list of allowed values
        if (allowedValues.length > 0) {
            settings.criteriaValues.value = allowedValues;
        } else if (field.type === FieldType.DateTime) {
            settings.criteriaValues.value = TodayMacros;
        } else {
            settings.criteriaValues.value = [];
        }

        settings.criteriaValues.ready.value = true;
    }

    private async _getAllowedValues(field: IFieldEntry): Promise<string[]> {
        const allowedValues = this._useNewFieldService
                ? await this._fieldService2.getAllowedValues(field.referenceName)
                : await this._fieldService.getAllowedValues(field.referenceName);
        if (!allowedValues) {
            this._dataProviderErrorMessage.value = this._useNewFieldService
                    ? this._fieldService2.getFieldAllowedValuesErrorMessage()
                    : this._fieldService.getFieldAllowedValuesErrorMessage();
        }

        return allowedValues || [];
    }

    public updateOperator(operator: string, item: ICriteriaSettingData, skipValidation?: boolean) {
        if (item) {
            item.operator = operator;
            !skipValidation && this._validate();
        }
    }

    public updateValue(value: string, item: ICriteriaSettingData, skipValidation?: boolean) {
        if (item) {
            const field = this._useNewFieldService
                ? this._fieldService2.getFieldByReferenceName(item.field)
                : this._fieldService.getFieldByReferenceName(item.field);
            if (field?.type === FieldType.DateTime && value === "") {
                item.criteriaValue.value = new Date().toLocaleDateString();
            } else {
                item.criteriaValue.value = value;
            }

            if (!this._validateCriteriaSetting(item)) {
                item.hasError.value = true;
            } else if (item.hasError.value) {
                item.hasError.value = false;
            }

            !skipValidation && this._validate();
        }
    }

    private _getDefaultCriteria(criteriaSetting?: ICriteriaSelectedSettingData): ICriteriaSettingData {
        const defaultSelectionIndex = 0;
        const defaultReferenceName = this._defaultField.referenceName;
        const operators = this._operators[defaultReferenceName];
        const operator = operators[defaultSelectionIndex];
        const id = newGuid();

        const criteriaSettingsData = {
            id,
            field: criteriaSetting?.field || defaultReferenceName,
            operator: criteriaSetting?.operator ? getLocalizedOperator(criteriaSetting.operator) : operator,
            criteriaValue: new ObservableValue(getLocalizedFieldValue(criteriaSetting?.criteriaValue.value) || ""),
            fields: this._fieldListBoxItems,
            operators: new ObservableArray(this._getListBoxItems(operators)),
            criteriaValues: new ReadyableObservableArray([], false),
            fieldSelection: new DropdownSelection(),
            operatorSelection: new DropdownSelection(),
            hasError: new ObservableValue(false)
        };
        // update selections
        criteriaSettingsData.fieldSelection.select(defaultSelectionIndex);
        criteriaSettingsData.operatorSelection.select(defaultSelectionIndex);

        return criteriaSettingsData;
    }

    private _validateCriteria() {
        this._rows.value.forEach(row => {
            const settings = row.criteriaData.value;

            if (!this._validateUniqueCriteria(settings)) {
                this._validationErrorMessage.value = Resources.DuplicateFieldCriteriaErrorMessage;
                return;
            }
            // check that all values are valid
            else if (settings.some(setting => setting.hasError.value)) {
                this._validationErrorMessage.value = Resources.InvalidCriteriaValueErrorMessage;
                return;
            }
            // check that there are no empty selections
            else if (settings.length <= 0) {
                this._validationErrorMessage.value = Resources.NoCriteriaErrorMessage;
                return;
            } else if (settings.findIndex(s => !s.field || !s.operator) >= 0) {
                // empty to show that the model isn't valid, but there is no error message to display
                this._validationErrorMessage.value = "";
                return;
            }
        });
    }

    private _validateUniqueCriteria(settings: ICriteriaSettingData[]): boolean {
        const uniqueCriteriaSettings: { [fieldId: string]: string[] } = {};
        for (let i = 0, len = settings.length; i < len; i++) {
            const setting = settings[i];
            const operatorAndValue = setting.operator + setting.criteriaValue.value;
            if (!uniqueCriteriaSettings[setting.field]) {
                uniqueCriteriaSettings[setting.field] = [];
            } else if (setting.operator.length > 0 && uniqueCriteriaSettings[setting.field].indexOf(operatorAndValue) >= 0) {
                // found a duplicate
                return false;
            }

            uniqueCriteriaSettings[setting.field].push(operatorAndValue);
        }

        return true;
    }

    private _validateCriteriaSetting(criteriaSetting: ICriteriaSettingData): boolean {
        const field = this._useNewFieldService 
                ? this._fieldService2.getFieldByReferenceName(criteriaSetting.field)
                : this._fieldService.getFieldByReferenceName(criteriaSetting.field);
        if (!field) {
            return false;
        }

        // Date fields can have macros
        if (field.type === FieldType.DateTime) {
            const value = criteriaSetting.criteriaValue.value;
            if (isTodayMacro(value, /* localized */ true)) {
                const invariantValue = getInvariantTodayMacro(value);
                const parsedTodayMacro = parseTodayMacro(invariantValue, /* useInvariant */ true);
                if (parsedTodayMacro) {
                    const { offset, modifier } = parsedTodayMacro;
                    // need both offset and modifier to be set or both to be undefined
                    return !!offset === !!modifier;
                } else {
                    // if the macro couldn't be parsed it's invalid
                    return false;
                }
            } else {
                return !!Date.parse(value);
            }
        } else if (field.referenceName === CoreFieldRefNames.IterationPath) {
            const value = criteriaSetting.criteriaValue.value;
            if (isCurrentIterationMacro(value, /* localized */ true)) {
                // check that any current iteration macro is valid
                const invariantValue = getInvariantCurrentIterationMacro(value);
                return !!parseCurrentIteration(invariantValue, /* useInvariant */ true);
            }
        }

        return true;
    }

    private _getDefaultRule(rule?: IStylesRuleData): IStylesRuleData {
        const id = newGuid();

        const defaultRulesData = {
            id,
            ruleName: rule?.ruleName ?? new ObservableValue<string>(""),
            color: rule?.color ?? new ObservableValue<string>("#FFFFFF"),
            titleColor: rule?.titleColor ?? new ObservableValue<string>("#000000"),
            isEnabled: rule?.isEnabled ?? new ObservableValue<boolean>(true),
            isTitleItalicized: rule?.isTitleItalicized ?? new ObservableValue<boolean>(false),
            isTitleBold: rule?.isTitleBold ?? new ObservableValue<boolean>(false),
            isTitleUnderlined: rule?.isTitleUnderlined ?? new ObservableValue<boolean>(false),
            filter: "",
            selectedCriteriaData: [],
            criteriaData: new ObservableArray<ICriteriaSettingData>([])
        };

        if (rule?.selectedCriteriaData) {
            this._addCriteriaSettingsRows(rule?.selectedCriteriaData);
        }

        return defaultRulesData;
    }

    private _getListBoxItems(options: string[]): IListBoxItem[] {
        return options.map(option => {
            return { id: option, text: option } as IListBoxItem;
        });
    }

    /**
     * Return localized supported operators for the given field
     */
    private _getLocalizedSupportedWiqlOperators(field: IFieldEntry): string[] {
        if (field.isIdentity) {
            return StyleCriteriaConstants.equalityOperators;
        }

        switch (field.type) {
            case FieldType.String:
                return StyleCriteriaConstants.stringOperators;
            case FieldType.Integer:
            case FieldType.DateTime:
            case FieldType.Double:
                return StyleCriteriaConstants.comparisonOperators;
            case FieldType.PlainText:
                return field.isTextQuerySupported ? StyleCriteriaConstants.textWithTextSupportOperators : StyleCriteriaConstants.historyOperators;
            case FieldType.TreePath:
                return StyleCriteriaConstants.treePathOperators;
            case FieldType.Boolean:
                return StyleCriteriaConstants.equalityOperators;
            default:
                throw new Error("The given field is not supported");
        }
    }

    private _setIsValid() {
        const valid = this._validationErrorMessage.value === undefined;

        if (this._isValid.value !== valid) {
            this._isValid.value = valid;
        }
    }

    private _setIsDirty() {
        if (!this._originalRules || !this._originalCriteria) {
            return;
        }

        const currentRules = this._rows.value as IStylesRuleData[];
        let dirty = this._originalRules.length != currentRules.length;
        if (!dirty) {
            dirty = this._originalRules.findIndex((criteria, index) => !areRulesEqual(criteria, currentRules[index])) >= 0;
        }

        if (!dirty) {
            for (var i = 0; i < this._originalCriteria.length; i++) {
                const currentCriteria = this._rows.value[i].criteriaData.value as ICriteriaSelectedSettingData[];
                dirty = this._originalCriteria[i].length != currentCriteria.length;
                if (!dirty) {
                    dirty = this._originalCriteria[i].findIndex((criteria, ind) => !areCriteriaEqual(criteria, currentCriteria[ind])) >= 0;
                }
                if (dirty) {
                    break;
                }
            }
        }

        if (this._isDirty.value !== dirty) {
            this._isDirty.value = dirty;
        }
    }
}

function areCriteriaEqual(a: ICriteriaSelectedSettingData, b: ICriteriaSelectedSettingData): boolean {
    return (
        getInvariantOperator(a.criteriaValue.value) === getInvariantOperator(b.criteriaValue.value) &&
        a.field === b.field &&
        getInvariantOperator(a.operator) === getInvariantOperator(b.operator)
    );
}

function areRulesEqual(a: IRuleData, b: IStylesRuleData): boolean {
    return (
        a.ruleName === b.ruleName.value &&
        a.isEnabled === b.isEnabled.value &&
        a.color === b.color.value &&
        a.titleColor == b.titleColor.value &&
        a.isTitleBold === b.isTitleBold.value &&
        a.isTitleItalicized === b.isTitleItalicized.value &&
        a.isTitleUnderlined === b.isTitleUnderlined.value
    );
}

function getLocalizedFieldValue(value: string | undefined): string | undefined {
    if (!value) {
        return;
    }

    if (isTodayMacro(value, /* localized */ false)) {
        return getLocalizedTodayMacro(value);
    } else if (isCurrentIterationMacro(value, /* localized */ false)) {
        return getLocalizedCurrentIterationMacro(value);
    }

    return value;
}

const serviceName = "IStyleService";
Services.add(serviceName, { serviceFactory: StyleService });
